// ════════════════════════════════════════════════════════════════════════
//  Ein neuer Lehrgang: leer oder als Kopie
//
//  Reine Rechnung, ohne React und ohne Datenbank.
// ════════════════════════════════════════════════════════════════════════

import { FINGER_IDS, OTTO_BUNDLE, ROW_IDS, type Bundle } from "@otto/lehrgang";
import type { Curriculum } from "./data.js";
import {
  newAssessmentKey,
  newLessonKey,
  newStageKey,
} from "../../../shared/lib/contentKeys.js";

/**
 * Ein leerer Lehrgang: Tastatur, Finger und Sternschwellen vorbelegt, keine
 * Lektion.
 *
 * Vorbelegt sind genau die Dinge, die keine Entscheidung der Redaktion sind: die
 * deutsche T1-Tastatur hat fünf Reihen, Hände haben neun einsetzbare Finger, und
 * die Sternschwellen stehen zusätzlich im SQL. Wer einen Lehrgang anlegt, will
 * Lektionen schreiben und nicht eine Tastatur abmalen.
 *
 * Ohne Lektion trägt der Entwurf einen Hinweis („Der Lehrgang hat noch keine
 * Lektion“) und lässt sich deshalb nicht veröffentlichen. Er lässt sich aber
 * anlegen, laden, speichern und bearbeiten – das ist der Unterschied zwischen
 * „noch nicht fertig“ und „kaputt“, und er steht im Schema: `lessons` hat kein
 * Minimum, `checkBundle` hat den Hinweis.
 */
export function emptyBundle(slug: string, title: string): Bundle {
  return {
    ...OTTO_BUNDLE,
    slug,
    title,
    lessons: [],
  };
}

/**
 * Ein Lehrgang als Kopie eines bestehenden – mit DURCHGEHEND NEUEN SCHLÜSSELN.
 *
 * Das ist der Zweck dieser Funktion, nicht ein Detail davon. `progress` hängt an
 * (lesson_key, stage_key) ohne jeden Lehrgangsbezug: zwei Lehrgänge mit
 * denselben Stufenschlüsseln teilen sich still den Fortschritt der Kinder. Ein
 * Kind, das in Grundkurs 2 eine Stufe übt, bekäme Sterne in Grundkurs 1 – und
 * niemand käme darauf, warum.
 *
 * `taken` sind alle bereits vergebenen Schlüssel, über ALLE Lehrgänge. Die
 * Prüfung vor dem Veröffentlichen wiederholt das; hier wird verhindert, dass es
 * überhaupt so weit kommt.
 */
export function copyBundle(
  source: Bundle,
  slug: string,
  title: string,
  taken: ReadonlySet<string> = new Set(),
  random: () => number = Math.random,
): Bundle {
  const used = new Set(taken);
  const lessons = source.lessons.map((lesson) => {
    const key = newLessonKey(null, used, random);
    used.add(key);
    const stages = lesson.stages.map((stage) => {
      const stageKey = newStageKey(null, used, random);
      used.add(stageKey);
      return { ...stage, key: stageKey };
    });
    return { ...lesson, key, stages };
  });

  // Der Test wird mitgewürfelt. Ohne das teilten sich Vorlage und Kopie die
  // Messungen der Kinder – derselbe Fall wie bei den Stufen, nur unsichtbarer,
  // weil es je Lehrgang nur einen Test gibt.
  const assessment = source.assessment
    ? { ...source.assessment, key: newAssessmentKey(null, used, random) }
    : undefined;

  return assessment
    ? { ...source, slug, title, lessons, assessment }
    : { ...source, slug, title, lessons };
}

/** Für die Vollständigkeit der Vorlage – beides sind Konstanten, keine Inhalte. */
export const TEMPLATE_SIZES = {
  rows: ROW_IDS.length,
  fingers: FINGER_IDS.length,
} as const;

/**
 * Der eingebaute Lehrgang als Startpunkt einer FRISCHEN Umgebung.
 *
 * Die Schlüssel bleiben, wie sie sind – anders als bei einer Kopie. Das ist der
 * Punkt: `l-fx25watu` soll auf Staging, in Produktion und im Code dieselbe Stufe
 * meinen. Wer einen Fehlerbericht liest, muss den Schlüssel nicht erst
 * übersetzen.
 *
 * Genau deshalb darf es das nur EINMAL geben, und deshalb bietet
 * `offersBuiltIn` es auch nur einmal an.
 */
export function builtInBundle(slug: string, title: string): Bundle {
  return { ...OTTO_BUNDLE, slug, title };
}

/**
 * Steht der eingebaute Lehrgang als Vorlage zur Wahl?
 *
 * Nur, solange nichts veröffentlicht ist. Danach wäre er eine zweite Quelle
 * derselben Stufenschlüssel – und zwei Lehrgänge, die sich Schlüssel teilen,
 * teilen sich still den Fortschritt der Kinder. Ab dann ist „Kopie von“ das
 * richtige Werkzeug; die vergibt neu.
 *
 * Er ersetzt den früheren Automatismus, mit dem „Ersten Entwurf anlegen“ das
 * Otto-Bundle einsetzte. Der galt für jeden Lehrgang ohne Fassung, also auch
 * für den zweiten – und machte ihn ungewollt zur Kopie des ersten.
 */
export function offersBuiltIn(curricula: readonly Curriculum[]): boolean {
  return curricula.every((c) => c.publishedVersion === null);
}

/** Kleinbuchstaben, Ziffern, Bindestriche – dieselbe Form wie im Schlüsselschema. */
export const SLUG_SHAPE = /^[a-z0-9]+(?:[-.][a-z0-9]+)*$/;

/** Womit ein neuer Lehrgang beginnt. */
export type Template =
  | { readonly kind: "empty" }
  | { readonly kind: "builtIn" }
  /** Kopie eines bestehenden. `bundleLoaded` ist falsch, solange sie noch lädt. */
  | { readonly kind: "copy"; readonly bundleLoaded: boolean };

/**
 * Darf „Anlegen und öffnen“ gedrückt werden?
 *
 * Als eigene Funktion und nicht als Ausdruck in der Komponente, weil genau hier
 * ein Fall vergessen wurde: die Bedingung verlangte eine geladene Quellfassung,
 * und der eingebaute Lehrgang hat keine – der Knopf blieb still grau. Eine
 * unvollständige boolesche Bedingung ist syntaktisch tadellos; nur ein Test
 * sieht sie.
 */
export function canCreate(input: {
  readonly slug: string;
  readonly title: string;
  /** Schon vergebene Kürzel. */
  readonly takenSlugs: readonly string[];
  readonly template: Template;
}): boolean {
  if (!SLUG_SHAPE.test(input.slug)) return false;
  if (input.takenSlugs.includes(input.slug)) return false;
  if (input.title.trim() === "") return false;
  // Nur die Kopie braucht etwas Geladenes. Leer und eingebaut stehen sofort da.
  return input.template.kind === "copy" ? input.template.bundleLoaded : true;
}
